/**
 * USGS Seismic Design Maps — ASCE 7-22 design parameters.
 * Free, no API key, lat/lng → JSON.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';

export interface SeismicData {
  pga: number;            // g, Peak Ground Acceleration
  sds: number;            // g, Design short-period spectral acceleration
  sd1: number;            // g, Design 1-second spectral acceleration
  designCategory: string; // A, B, C, D, E, F
  riskCategory: string;   // III for essential facilities
  siteClass: string;      // CD (default), A-F
}

/** Derive Seismic Design Category from SDS/SD1 per ASCE 7-22 Table 11.6-1/11.6-2.
 *  SDC F: SD1 >= 0.75g for Risk Category IV, or SD1 >= 0.50g for Risk Cat III (data centers).
 *  Per ASCE 7-22 Section 11.6, SDC F applies to high-seismic sites with essential facilities. */
export const SDC_ORDER: Record<string, number> = { A: 0, B: 1, C: 2, D: 3, E: 4, F: 5 };

function deriveSDC(sds: number, sd1: number): string {
  // SDC F: SD1 >= 0.75g (ASCE 7-22 §11.6 for Risk Category III essential facilities)
  if (sd1 >= 0.75) return 'F';

  const sdcFromSds = sds < 0.167 ? 'A' : sds < 0.33 ? 'B' : sds < 0.50 ? 'C' : sds < 0.75 ? 'D' : 'E';
  const sdcFromSd1 = sd1 < 0.067 ? 'A' : sd1 < 0.133 ? 'B' : sd1 < 0.20 ? 'C' : sd1 < 0.30 ? 'D' : 'E';
  // Take the more severe category using numeric comparison
  return (SDC_ORDER[sdcFromSds] ?? 0) >= (SDC_ORDER[sdcFromSd1] ?? 0) ? sdcFromSds : sdcFromSd1;
}

export const usgsSeismicProvider: DataProvider<SeismicData> = {
  id: 'usgs-seismic',
  name: 'USGS Seismic Design Maps (ASCE 7-22)',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<SeismicData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`usgs-seismic` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. Seismic hazard varies far slower than parcel scale — the
    // centroid usually sits in the same NSHM grid cell as the address — so
    // this is a consistency change, not a behaviour change in practice;
    // still the parcel centre is the right point to design to.
    // Risk Category III for essential/data center facilities
    const url = `https://earthquake.usgs.gov/ws/designmaps/asce7-22.json?latitude=${lat}&longitude=${lng}&riskCategory=III&siteClass=CD&title=SiteRAG`;

    const res = await fetchWithTimeout(url, 15_000, geo?.signal);
    if (!res.ok) throw new Error(`USGS: ${res.status} ${res.statusText}`);
    const json = await res.json();

    const response = json.response;
    if (!response?.data) throw new Error('USGS returned no seismic data');

    const data = response.data;
    // USGS ASCE 7-22 API returns PGA as 'pgam' (MCER ground motion), not 'pgaValue' or 'pga'
    const pga = data.pgam ?? data.pgaValue ?? data.pga ?? 0;
    const sds = data.sds ?? 0;
    const sd1 = data.sd1 ?? 0;
    // Use API-provided SDC when available, otherwise derive from SDS/SD1
    const designCategory = data.sdc ?? deriveSDC(sds, sd1);

    // Warn if PGA is still 0 after trying all fields — likely a new API format
    if (pga === 0 && sds > 0.1) {
      console.warn(`USGS PGA=0 with SDS=${sds} — check API response field names. Available: ${Object.keys(data).join(', ')}`);
    }

    return {
      data: { pga, sds, sd1, designCategory, riskCategory: 'III', siteClass: 'CD' },
      sources: [{
        field: 'seismic',
        source: `USGS Seismic Design Maps, ASCE 7-22 (Risk Cat III, Site Class CD)`,
        url: 'https://earthquake.usgs.gov/ws/designmaps/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
